import { useCallback, useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"
import fp from "lodash/fp"

import type { PlotReqConfig } from "types/dashboard.types"
import type {
  EntityTelemetry,
  BaseTelemetryParams,
  TelemetryFetcher,
  QualityFetcher,
} from "types/telemetries.types"
import type { DateRange } from "types/date.types"
import type { QualityRecord } from "types/quality.types"

import { isSuperseded } from "features/api/requestQueue"
import { getForecastThresholdDate, isWithinOneDay } from "helpers/utils/dateUtils"
import { aggregateQualityRecords, mergeQualityResults } from "helpers/utils/quality"

interface UseMultiEntityTelemetryProps {
  ids: number[]
  reqConfig: PlotReqConfig
  dateRange: DateRange | null
  isDateRangeValid: boolean
  enabled?: boolean
  fetchTelemetry: TelemetryFetcher
  fetchTelemetryQuality: QualityFetcher
}

export interface UseMultiEntityTelemetryReturn {
  data: EntityTelemetry[]
  isFetching: boolean
  qualityData: QualityRecord[]
  qualityFetching: boolean
  isRawData: boolean
  error: unknown | null
  qualityError: unknown | null
  refetch: () => void
}

export const useMultiEntityTelemetry = ({
  ids,
  reqConfig,
  dateRange,
  isDateRangeValid,
  enabled = true,
  fetchTelemetry,
  fetchTelemetryQuality,
}: UseMultiEntityTelemetryProps): UseMultiEntityTelemetryReturn => {
  const [data, setData] = useState<EntityTelemetry[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [qualityData, setQualityData] = useState<QualityRecord[]>([])
  const [qualityFetching, setQualityFetching] = useState(false)
  const [error, setError] = useState<unknown | null>(null)
  const [qualityError, setQualityError] = useState<unknown | null>(null)
  const [refetchIndex, setRefetchIndex] = useState(0)
  const refetch = useCallback(() => {
    setRefetchIndex((current) => current + 1)
  }, [])

  const {
    aggregate,
    aggregate_n,
    aggregate_use_original_time,
    fields,
    field_prefix,
    group,
    method,
    aggregate_every,
    forecastFields,
    fetchQualitySummary,
    allowRawData,
    fetchFieldMetadata,
  } = reqConfig

  const isRawData = !!allowRawData && dateRange != null && isWithinOneDay(dateRange)

  const forecastThresholdDate = useMemo(
    () => getForecastThresholdDate(dateRange),
    [dateRange],
  )

  useEffect(() => {
    let cancelled = false

    if (
      enabled &&
      ids.length &&
      isDateRangeValid &&
      dateRange &&
      fields &&
      fields.length
    ) {
      setIsFetching(true)

      const params: BaseTelemetryParams = {
        ...(isRawData
          ? {
              ...(fetchFieldMetadata && { include_field_metadata: true }),
            }
          : {
              aggregate,
              aggregate_n,
              aggregate_use_original_time,
              aggregate_every,
            }),
        method,
        group,
        field: fields,
        field_prefix,
        from_date: dateRange.fromDate,
        to_date: dateRange.toDate,
      }

      Promise.allSettled(
        ids.map((id) => fetchTelemetry(id, params, refetchIndex === 0)),
      ).then((results) => {
        if (cancelled) return
        const rejected = results.filter(
          (r): r is PromiseRejectedResult => r.status === "rejected",
        )
        // The range moved on and the newer wave owns this plot now. Landing
        // this one would blank the chart or show its cancellation as a failure.
        if (rejected.some((r) => isSuperseded(r.reason))) return
        const fulfilled = results.filter(
          (r): r is PromiseFulfilledResult<EntityTelemetry[]> => r.status === "fulfilled",
        )
        setData(fulfilled.flatMap((r) => r.value))
        setError(rejected.length > 0 ? rejected[0].reason : null)
        setIsFetching(false)
      })
    } else {
      setIsFetching(false)
    }

    return () => {
      cancelled = true
    }
  }, [
    fetchTelemetry,
    ids,
    isDateRangeValid,
    dateRange,
    fields,
    field_prefix,
    aggregate,
    aggregate_n,
    aggregate_use_original_time,
    method,
    group,
    aggregate_every,
    isRawData,
    fetchFieldMetadata,
    enabled,
    refetchIndex,
  ])

  useEffect(() => {
    let cancelled = false

    if (
      enabled &&
      fetchQualitySummary &&
      ids.length &&
      isDateRangeValid &&
      dateRange &&
      fields &&
      fields.length
    ) {
      setQualityFetching(true)
      setQualityError(null)

      const regularFieldsList = forecastFields?.length
        ? fp.difference(fields)(forecastFields)
        : fields
      const forecastFieldsList = forecastFields ?? []

      const regularPromise = regularFieldsList.length
        ? Promise.all(
            ids.map((id) =>
              fetchTelemetryQuality(id, {
                field: regularFieldsList,
                from_date: dateRange.fromDate,
                to_date: dateRange.toDate,
              }),
            ),
          )
        : Promise.resolve([] as QualityRecord[][])

      const hasForecastRange =
        forecastThresholdDate &&
        forecastThresholdDate < DateTime.fromISO(dateRange.toDate)

      const forecastPromise =
        forecastFieldsList.length && hasForecastRange
          ? Promise.all(
              ids.map((id) =>
                fetchTelemetryQuality(id, {
                  field: forecastFieldsList,
                  from_date: String(forecastThresholdDate.toISO()),
                  to_date: dateRange.toDate,
                }),
              ),
            )
          : Promise.resolve([] as QualityRecord[][])

      Promise.all([regularPromise, forecastPromise])
        .then(([regularResults, forecastResults]) => {
          if (!cancelled) {
            const regularQuality = regularResults.length
              ? aggregateQualityRecords(regularResults.flat())
              : []
            const forecastQuality = forecastResults.length
              ? aggregateQualityRecords(forecastResults.flat())
              : []

            const mergedData =
              regularQuality.length && forecastQuality.length
                ? mergeQualityResults(regularQuality, forecastQuality)
                : regularQuality.length
                  ? regularQuality
                  : forecastQuality

            setQualityData(mergedData)
            setQualityFetching(false)
          }
        })
        .catch((err) => {
          // Same as the series above: a superseded summary belongs to a range
          // the chart has already left.
          if (cancelled || isSuperseded(err)) return
          setQualityData([])
          setQualityFetching(false)
          setQualityError(err)
        })
    } else {
      setQualityFetching(false)
    }

    return () => {
      cancelled = true
    }
  }, [
    fetchTelemetryQuality,
    ids,
    isDateRangeValid,
    dateRange,
    fields,
    forecastFields,
    fetchQualitySummary,
    enabled,
    refetchIndex,
    forecastThresholdDate,
  ])

  return {
    data,
    isFetching,
    qualityData,
    qualityFetching,
    isRawData,
    error,
    qualityError,
    refetch,
  }
}
